import React, { useState, useEffect, useCallback } from 'react';
import { Container, ButtonGroup, Button, Modal, Alert } from 'react-bootstrap';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import debounce from 'lodash.debounce';
import { ReactComponent as ArrowUpRight } from '../../static/images/svg/Arrow up right.svg';
import { ReactComponent as Trash } from '../../static/images/svg/Trash.svg';
import DetailsTable from '../../components/table';
import Footer from '../../components/footer';
import OffCanvasComp from '../../components/offcanvas';
import SpinnerComp from '../../components/spinner';
import SearchBar from '../../components/searchbar';
import Filters from '../../components/date-and-vendor-filter/index'; 
import DateFilter from '../../components/datefilter/index'; 

import CustomButton from '../../components/button';

function AllShipments({ user, isAnalytics=false }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStatusSm, setFilterStatusSm] = useState('');

  const [orderItems, setOrderItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [show, setShow] = useState(false);
  const [orderItem, setOrderItem] = useState();
  const [modalShow, setModalShow] = useState(false);
  const [shipmentToDelete, setShipmentToDelete] = useState(null);
  const [totalItem, setTotalItem] = useState();

  // New State for Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [vendors, setVendors] = useState([]); // Populate this from an API or static data
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [riders, setRiders] = useState([]);
  const [selectedRider, setSelectedRider] = useState(null);
  const [selectedRiderId, setSelectedRiderId] = useState("");
  const pageSize = 8;

  const fetchShipments = useCallback(
    debounce(async (searchQuery, filterStatusSm, currentPage) => {
      let isEror = false
      try {
        if (fromDate && !toDate) {
          toast.warn("To Date is missing.")
          isEror=true
        }if (!fromDate && toDate) {
          toast.warn("From Date is missing.")
          isEror=true
        }
        if (fromDate && toDate && new Date(toDate) < new Date(fromDate)) {          
          toast.error("To Date cannot be earlier than From Date.")
          isEror=true
        }
        
        if (toDate && new Date().toLocaleString('en-GB', { timeZone: 'Asia/Dubai' }) < new Date(toDate)) {
          toast.error("To Date cannot be later than current Date.")
          isEror=true
        }
        if (!isEror) {
          setLoading(true);
        
        const response = await axios.get(
          `${process.env.REACT_APP_DEV_BACKEND_URL}/shipment/all`,
          {
            params: {
              page: currentPage,
              pageSize,
              search: searchQuery,
              status: filterStatusSm,
              fromDate: fromDate || null,
              toDate: toDate || null,
              vendorId: selectedVendorId,
              riderId: selectedRiderId

            },
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );
        
        
        if (response.status === 200) {          
          const { shipments, pagination } = response.data;
            setOrderItems(shipments);
            setFilteredItems(shipments);
            setTotalPages(pagination.totalPages);
            setTotalItem(pagination.totalItems)

          }
        }
        
      } catch (error) {
        toast.error('Failed to fetch shipments!');
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }, 1000),
    [fromDate, toDate, selectedVendorId, selectedRiderId]
  );

  useEffect(() => {
    fetchShipments(searchQuery, filterStatusSm, currentPage);
    if (user.role === "admin") {
      fetchVendors();
      fetchRiders()
    }
  }, [searchQuery, filterStatusSm, currentPage, fetchShipments]);

  const fetchRiders = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DEV_BACKEND_URL}/users/all-user`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          params: { status: "active", role: "rider" },
        }
      );
      
      setRiders(
        response.data.sellers.map((rider) => ({
          value: rider._id,
          label: rider.name,
        }))
      );
    } catch (err) {
      console.error("Error fetching riders:", err);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DEV_BACKEND_URL}/users/all-user`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          params: { status: "active", role: "vendor" },

        }
      );
      
      setVendors(response.data.sellers.map((sellers) => ({ value: sellers._id, label: sellers.name })));
      
    } catch (err) {
      console.error("Error fetching vendors:", err);
    }
  };
  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleFilter = (status) => {
    
    setFilterStatus(status);
    setCurrentPage(1); // Reset to first page on filter change
  };


  const handleFilterSm = (status) => {
    
    setFilterStatusSm(status);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleReset = () => {
    setFilterStatus("")
    setFilterStatusSm("")
    setSearchQuery("")
    setFromDate('');
    setToDate('');
    setSelectedVendor(null);
    setSelectedVendorId(null);
    setSelectedRider(null)
    setSelectedRiderId(null)
    fetchShipments(searchQuery, '', 1); // Reset filters and reload data
  };

  const confirmDelete = (item) => {
    setShipmentToDelete(item._id);
    setModalShow(true);
  };

  const deleteShipment = async (shipmentId) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_DEV_BACKEND_URL}/shipment/delete/${shipmentId}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success('Shipment deleted successfully!');
        fetchShipments(searchQuery, filterStatus, currentPage);
      }
    } catch (error) {
      toast.error('Error deleting shipment!');
      console.error('Error deleting shipment:', error);
    } finally {
      setModalShow(false);
    }
  };

  const columns = [
    {
      header: 'Barcode#',
      render: (item) => item.shipmentId,
    },
    {
      header: 'Status',
      render: (item) => item.status,
    },
    {
      header: 'Rider',
      render: (item) => item.rider? item.rider.name:"Not Assigned",
    },
    {
      header: 'Receiver',
      render: (item) => item.receiverName,
    },
    {
      header: 'Charges',
      render: (item) => item.charges,
    },
    {
      header: 'Date',
      render: (item) => new Date(item.createdAt).toLocaleString('en-GB', { timeZone: 'Asia/Dubai' }),
    },


    {
      header: 'Action',
      render: (item) => (
        <div className="d-flex justify-content-center gap-2">
          <ArrowUpRight onClick={() => handleButtonClick(item)}style={{ cursor: 'pointer' }} />
          {user.role !== 'admin' && (
            <Trash
              onClick={() => item.status === 'pending' ? confirmDelete(item) : null}
              style={{
                cursor: item.status === 'pending' ? 'pointer' : 'not-allowed',
                opacity: item.status === 'pending' ? 1 : 0.5,
              }}
            />
          )}
        </div>
      ),
    },
  ];

	const handleButtonClick = (item) => {
		setOrderItem(item)
		setShow(true)
	}


  return (
    <>
      {loading ? (
        <SpinnerComp />
      ) : (
        <Container fluid className="px-4 bg-light p-4" style={{minHeight:"92vh"}}>
          <h2 style={{ color: '#005A9C' }}>All Shipments</h2>
          <ToastContainer/>
          <SearchBar value={searchQuery} placeholder={"Search Barcode..."} onChange={(e) => handleSearch(e.target.value)} />
          <br/>
          {!isAnalytics && (
            user.role === "admin" ? (
              <Filters
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={setFromDate}
                setToDate={setToDate}
                vendors={vendors}
                selectedVendor={selectedVendor}
                setSelectedVendor={setSelectedVendor}
                selectedVendorId={selectedVendorId}
                setSelectedVendorId={setSelectedVendorId}
                riders={riders}
                selectedRider={selectedRider}
                setSelectedRider={setSelectedRider}
                selectedRiderId={selectedRiderId}
                setSelectedRiderId={setSelectedRiderId}
                handleFilter={handleFilter}
                handleReset={handleReset}
                isAdmin={user.role === 'admin'}
                showApplyButton={false}
              />
            ) : (
              <DateFilter
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={setFromDate}
                setToDate={setToDate}
                handleFilter={handleFilter}
                handleReset={handleReset}
                showApplyButton={false}
              />
            )
          )}

          <div className="my-3">
            <ButtonGroup className="d-none d-sm-flex"> {/* Default horizontal layout for larger screens */}
              {['All', 'Delivered', 'In Transit', 'Rescheduled', 'Pending', 'Cancelled'].map((status) => (
                <Button
                  key={status}
                  variant={filterStatusSm === status ? 'primary' : 'outline-primary'}
                  onClick={() => handleFilterSm(status === 'All' ? '' : status)}
                >
                  {status}
                </Button>
              ))}
            </ButtonGroup>
            <div className="d-flex d-sm-none flex-column gap-2"> {/* Stacked layout for smaller screens */}
              {['All', 'Delivered', 'In Transit', 'Rescheduled', 'Pending', 'Cancelled'].map((status) => (
                <Button
                  key={status}
                  variant={filterStatusSm === status ? 'primary' : 'outline-primary'}
                  onClick={() => handleFilterSm(status === 'All' ? '' : status)}
                >
                  {status}
                </Button>
              ))}
            </div>
          </div>

          <br/>
          <div className="border" style={{ height: '27rem', overflowY: 'auto' }}>
            {filteredItems.length === 0 ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
              <Alert className="text-center" style={{ width: '50%', backgroundColor:"#005A9C", color:"white"}}>
                No shipment found.
              </Alert>
              </div>
            ) : (
              <DetailsTable isTransparent={true} data={filteredItems} columns={columns} />
            )}
          </div>
          <Footer
            className={'d-flex justify-content-between align-items-center pt-3'}
            text={`${totalItem} Total Count`}
            pageSize={pageSize}
            totalPages={totalPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </Container>
      )}

      {show && (
        <OffCanvasComp
          placement={'end'}
          show={show}
          setShow={setShow}
          orderItem={orderItem}
          name={user.name}
          token={user.token}
          role={user.role}
        />
        
      )}

      <Modal show={modalShow} onHide={() => setModalShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this shipment?</Modal.Body>
        <Modal.Footer>
          <CustomButton variant="secondary" onClick={() => setModalShow(false)}>
            Cancel
          </CustomButton>
          <CustomButton variant="danger" onClick={() => deleteShipment(shipmentToDelete)}>
            Delete
          </CustomButton>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default AllShipments;
